import React, { useState } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Divider,
  Chip,
  Avatar,
  Collapse
} from '@mui/material';
import {
  Dashboard,
  Inventory,
  Notifications,
  SwapHoriz,
  Assessment,
  Warehouse,
  Settings,
  CheckCircle,
  Wifi,
  Timeline,
  People,
  Security,
  ExpandMore,
  ExpandLess,
  History,
  MenuBook,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAppData } from '../../context/AppDataContext';

const menuItems = [
  {
    text: 'Dashboard',
    icon: Dashboard,
    path: '/dashboard',
    color: '#3b82f6',
    menuPermission: 'menu:dashboard'
  },
  {
    text: 'Inventory Import',
    icon: Inventory,
    path: '/inventory-import',
    color: '#10b981',
    menuPermission: 'menu:inventory-import'
  },
  {
    text: 'Inventory Transfer',
    icon: SwapHoriz,
    path: '/inventory-transfer',
    color: '#06b6d4',
    menuPermission: 'menu:inventory-transfer'
  },
  {
    text: 'Inventory Count History',
    icon: History,
    path: '/inventory-count-history',
    color: '#7c3aed',
    menuPermission: 'menu:inventory-inspection'
  },
  {
    text: 'Notification Log',
    icon: Notifications,
    path: '/notifications',
    color: '#f59e0b',
    // No menuPermission - always visible to all users
  },
  {
    text: 'Report',
    icon: Assessment,
    path: '/reports',
    color: '#ef4444',
    menuPermission: 'menu:report'
  },
  {
    text: 'Tag Timeline',
    icon: Timeline,
    path: '/tag-timeline',
    color: '#7c3aed',
    menuPermission: 'menu:tag-timeline'
  },
  {
    text: 'User, Roles & Permissions',
    icon: Security,
    path: '/user-roles-permissions',
    color: '#7c2d12',
    menuPermission: 'menu:user-roles-permissions'
  },
  {
    text: 'Documents',
    icon: MenuBook,
    path: '/documents',
    color: '#8b5cf6',
    // No menuPermission - always visible to all users
  },
  {
    text: 'Settings',
    icon: Settings,
    path: '/settings',
    color: '#64748b',
    // No menuPermission - always visible to all users
  }
];

const Sidebar = ({ onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const { isMenuVisible, frontendPermissions, permissions } = useAppData();

  const handleNavigation = (path) => {
    navigate(path);
    if (onClose) onClose();
  };

  const handleSubmenuToggle = (itemText) => {
    setOpenSubmenu(openSubmenu === itemText ? null : itemText);
  };

  // Filter menu items based on permissions
  const visibleMenuItems = menuItems.filter(item => {
    const hasPermission = !item.menuPermission || isMenuVisible(item.menuPermission);
    return hasPermission;
  });

  // Log permissions on mount

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header with Logo */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        <Toolbar sx={{ 
          background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
          minHeight: 80,
          position: 'relative',
          overflow: 'hidden'
        }}>
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: 'radial-gradient(circle at 30% 20%, rgba(255,255,255,0.1) 0%, transparent 50%)',
              zIndex: 0
            }}
          />
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2, 
            width: '100%',
            position: 'relative',
            zIndex: 1
          }}>
            <img 
              src="/img/logo.png" 
              alt="BridgeX Logo" 
              style={{
                height: '40px',
                width: 'auto',
                objectFit: 'contain',
                filter: 'brightness(0) invert(1)'
              }}
            />
            <Box>
              <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, fontSize: '1.25rem' }}>
                BridgeX WMS
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.75rem' }}>
                v2.0.1
              </Typography>
            </Box>
          </Box>
        </Toolbar>
      </motion.div>

      {/* Main Warehouse Label */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.4, delay: 0.2 }}
      >
        <Box sx={{ p: 3 }}>
          <Chip
            label="MAIN WAREHOUSE"
            size="small"
            sx={{
              width: '100%',
              backgroundColor: '#f1f5f9',
              color: '#1e293b',
              fontWeight: 600,
              borderRadius: 1,
              fontSize: '0.75rem',
              border: '1px solid #e2e8f0',
              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
            }}
          />
        </Box>
      </motion.div>

      {/* Navigation Menu */}
      <List sx={{ px: 2, flex: 1 }}>
        {visibleMenuItems.map((item, index) => {
          const Icon = item.icon;
          const hasSubmenu = item.submenu && item.submenu.length > 0;
          const isSubmenuOpen = openSubmenu === item.text;
          
          // Check if this item or any of its submenu items are active
          const isActive = location.pathname === item.path || 
            (hasSubmenu && item.submenu.some(sub => location.pathname === sub.path));
          
          return (
            <motion.div
              key={item.text}
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: 0.3 + index * 0.05 }}
            >
              <ListItem disablePadding sx={{ mb: 0.5, flexDirection: 'column', alignItems: 'stretch' }}>
                <ListItemButton
                  onClick={() => hasSubmenu ? handleSubmenuToggle(item.text) : handleNavigation(item.path)}
                  sx={{
                    minHeight: 48,
                    borderRadius: 0.5,
                    mx: 1,
                    backgroundColor: isActive ? '#eff6ff' : 'transparent',
                    border: isActive ? `2px solid ${item.color}` : '2px solid transparent',
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      backgroundColor: isActive ? '#eff6ff' : '#f8fafc',
                      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                      transform: 'translateX(4px)',
                    },
                    '&::before': isActive ? {
                      content: '""',
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: 4,
                      backgroundColor: item.color,
                      borderRadius: '0 1px 1px 0'
                    } : {}
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 40,
                      color: isActive ? item.color : '#64748b'
                    }}
                  >
                    <Icon sx={{ fontSize: 22 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: isActive ? 600 : 500,
                          color: isActive ? '#1e293b' : '#475569',
                          fontSize: '0.875rem'
                        }}
                      >
                        {item.text}
                      </Typography>
                    }
                  />
                  {hasSubmenu ? (
                    isSubmenuOpen ? <ExpandLess sx={{ fontSize: 20, color: '#64748b' }} /> : <ExpandMore sx={{ fontSize: 20, color: '#64748b' }} />
                  ) : isActive ? (
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: item.color,
                        boxShadow: `0 0 8px ${item.color}40`
                      }}
                    />
                  ) : null}
                </ListItemButton>
                
                {/* Submenu */}
                {hasSubmenu && (
                  <Collapse in={isSubmenuOpen} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {item.submenu.map((subItem, subIndex) => {
                        const SubIcon = subItem.icon;
                        const isSubActive = location.pathname === subItem.path;
                        
                        return (
                          <ListItemButton
                            key={subItem.text}
                            onClick={() => handleNavigation(subItem.path)}
                            sx={{
                              pl: 6,
                              minHeight: 44,
                              borderRadius: 0.5,
                              mx: 1,
                              my: 0.5,
                              backgroundColor: isSubActive ? '#f0f9ff' : 'transparent',
                              border: isSubActive ? `1px solid ${subItem.color}` : '1px solid transparent',
                              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                              '&:hover': {
                                backgroundColor: isSubActive ? '#f0f9ff' : '#f8fafc',
                                transform: 'translateX(4px)',
                              }
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                minWidth: 36,
                                color: isSubActive ? subItem.color : '#94a3b8'
                              }}
                            >
                              <SubIcon sx={{ fontSize: 20 }} />
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: isSubActive ? 600 : 500,
                                    color: isSubActive ? '#1e293b' : '#64748b',
                                    fontSize: '0.8125rem'
                                  }}
                                >
                                  {subItem.text}
                                </Typography>
                              }
                            />
                            {isSubActive && (
                              <Box
                                sx={{
                                  width: 6,
                                  height: 6,
                                  borderRadius: '50%',
                                  backgroundColor: subItem.color,
                                  boxShadow: `0 0 6px ${subItem.color}40`
                                }}
                              />
                            )}
                          </ListItemButton>
                        );
                      })}
                    </List>
                  </Collapse>
                )}
              </ListItem>
            </motion.div>
          );
        })}
      </List>

      <Divider sx={{ mx: 3, my: 2 }} />

      {/* System Status */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.8 }}
      >
        <Box sx={{ px: 3, py: 2 }}>
          <Typography 
            variant="caption" 
            sx={{ 
              color: '#64748b', 
              textTransform: 'uppercase', 
              letterSpacing: '0.05em',
              fontWeight: 600,
              fontSize: '0.75rem'
            }}
          >
            System Status
          </Typography>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: 0.9 }}
            >
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                p: 1.5,
                backgroundColor: '#f0fdf4',
                borderRadius: 1,
                border: '1px solid #bbf7d0',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  backgroundColor: '#dcfce7',
                  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                  transform: 'translateX(4px)'
                }
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Avatar sx={{ width: 24, height: 24, backgroundColor: '#10b981' }}>
                    <CheckCircle sx={{ fontSize: 16, color: 'white' }} />
                  </Avatar>
                  <Typography variant="body2" sx={{ color: '#374151', fontSize: '0.75rem', fontWeight: 500 }}>
                    RFID Scanner
                  </Typography>
                </Box>
                <Chip
                  label="Online"
                  size="small"
                  sx={{
                    backgroundColor: '#dcfce7',
                    color: '#166534',
                    fontSize: '0.625rem',
                    fontWeight: 600,
                    borderRadius: 0.5,
                    border: '1px solid #bbf7d0'
                  }}
                />
              </Box>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: 1.0 }}
            >
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                p: 1.5,
                backgroundColor: '#f0f9ff',
                borderRadius: 1,
                border: '1px solid #bae6fd',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  backgroundColor: '#e0f2fe',
                  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                  transform: 'translateX(4px)'
                }
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Avatar sx={{ width: 24, height: 24, backgroundColor: '#06b6d4' }}>
                    <Wifi sx={{ fontSize: 16, color: 'white' }} />
                  </Avatar>
                  <Typography variant="body2" sx={{ color: '#374151', fontSize: '0.75rem', fontWeight: 500 }}>
                    Connection
                  </Typography>
                </Box>
                <Chip
                  label="Connected"
                  size="small"
                  sx={{
                    backgroundColor: '#cffafe',
                    color: '#155e75',
                    fontSize: '0.625rem',
                    fontWeight: 600,
                    borderRadius: 0.5,
                    border: '1px solid #bae6fd'
                  }}
                />
              </Box>
            </motion.div>
          </Box>
        </Box>
      </motion.div>
    </Box>
  );
};

export default Sidebar;